import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, EventEmitter, Inject, Input, Output } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { LoaderService } from '@icc/b2b/sales-document';
import { APP_VERSION, DeliveryColumnKey, DeliveryColumnSortOrder, deliveryListColumns, DeliveryListItem, scrollTop } from '@icc/b2b/shared';
import { ConfigService } from '@icc/shared/config.service';
import { core, parseDate } from '@icc/shared/helpers';
import { TranslateService } from '@ngx-translate/core';
import { ReplaySubject, Subscription } from 'rxjs';

@Component({
    selector: 'icc-deliveries-list',
    templateUrl: './deliveries-list.component.html',
    styleUrls: ['./deliveries-list.component.scss'],
})
export class DeliveriesListComponent {
    static crumbsSubject = new ReplaySubject<Record<string, any>>();
    allDeliveries: DeliveryListItem[] = [];
    orderedDeliveries: DeliveryListItem[] = [];

    columnSortOptions: Partial<
        Record<
            DeliveryColumnKey,
            {
                order: 'asc' | 'desc' | null;
                index: number;
            } | null
        >
    > = {};
    columnsSortOrder: DeliveryColumnSortOrder = [];

    @Input() documentsInPage = 20;
    totalItems = 0;

    private _page = 1;
    @Input()
    get page(): number {
        return this._page;
    }
    set page(page: number) {
        this._page = page;
    }

    @Output() pageEmitter = new EventEmitter();

    subscriptions: Subscription[] = [];

    sortMethods: Record<DeliveryColumnKey, (a: DeliveryListItem, b: DeliveryListItem) => number> = {
        deliveryAddress: (a, b) => a.deliveryAddress.localeCompare(b.deliveryAddress),
        client: (a, b) => a.client.localeCompare(b.client),
        isConfirmed: (a, b) => a.isConfirmed === b.isConfirmed ? 0 : a.isConfirmed ? 1 : -1,
        created: (a, b) => a.created.getTime() - b.created.getTime(),
        modified: (a, b) => a.modified.getTime() - b.modified.getTime(),
    };


    mobileView = false;

    filtered = true;

    constructor(
        private router: Router,
        private configService:ConfigService,
        private route: ActivatedRoute,
        private translateService: TranslateService,
        private http: HttpClient,
        @Inject(APP_VERSION) private appVersion: string,
        private loaderService: LoaderService,
        private breakpointObserver: BreakpointObserver,
    ) {}


    onChangePage(page: number) {
        this.page = page;
        this.pageEmitter.emit(page);
        this.updateList(true);
        scrollTop();
    }

    ngOnInit(): void {
        this.subscriptions.push(
            this.breakpointObserver.observe(['(max-width: 990px)']).subscribe((result) => {
                this.mobileView = result.matches;
            })
        );
        this.setListCrumbs();
        this.updateList();
    }

    ngOnDestroy() {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }

    open(document: DeliveryListItem) {
        this.router.navigate(['transport', 'delivery', document.uuid]);
    }

    async updateList(updateRemote = false) {
        await this.getRemoteDocuments();
    }

    async getRemoteDocuments() {
        this.loaderService.setLoadingAfterTime();
        const serverDocuments = await this.http.post<{
            deliveries: DeliveryListItem[],
            limit: number,
            offset: number,
            totalCount: number,
        }>(`${window.location.origin}/api/transport/deliveries`, {
            userId: this.configService?.user?.user?.id,
            appVersion: this.appVersion,
            sort: this.columnsSortOrder || [],
            filters: {
                notConfirmed: this.filtered,
            },
            offset: (this.page - 1) * this.documentsInPage,
            limit: this.documentsInPage,
        }).toPromise();
        this.allDeliveries = serverDocuments.deliveries.map(doc => {
            doc.created = parseDate(doc.created);
            doc.modified = parseDate(doc.modified);
            return doc;
        });
        this.totalItems = serverDocuments.totalCount;
        this.loaderService.setLoaded();
    }

    private setColumnSortDefaults() {
        this.columnsSortOrder = [
            { column: 'created', order: 'desc' },
        ];
        this.setColumnSortOptions();
        this.saveConfiguration();
    }

    private setColumnSortOptions() {
        deliveryListColumns.forEach((el) => {
            this.columnSortOptions[el] = null;
        });

        this.columnsSortOrder.forEach((el, i) => {
            if (el.column) {
                this.columnSortOptions[el.column] = {
                    index: i + 1,
                    order: el.order,
                };
            }
        });
    }

    orderBy(column: DeliveryColumnKey) {
        const columnOrderIndex = this.columnsSortOrder.findIndex((el) => el.column === column);
        let columnOrder = null;
        if (columnOrderIndex > -1) {
            columnOrder = this.columnsSortOrder[columnOrderIndex];
            this.columnsSortOrder.splice(columnOrderIndex, 1);
        }
        this.columnsSortOrder.unshift({
            column,
            order: columnOrder?.order === 'asc' ? 'desc' : 'asc',
        });
        if (this.columnsSortOrder.length > 3) {
            this.columnsSortOrder.splice(3, this.columnsSortOrder.length - 3);
        }

        this.setColumnSortOptions();
        this.page = 1;
        this.updateList(true);
        this.saveConfiguration();
    }

    saveConfiguration() {
        sessionStorage.setItem(
            `deliveries-list-${
                this.configService?.user?.user?.id
            }`,
            core.stringJson({
                sort: this.columnsSortOrder,
                page: this.page,
            })
        );
    }

    loadConfiguration() {
        const { sort, page } = core.parseJson(
            sessionStorage.getItem(
                `deliveries-list-${
                    this.configService?.user?.user?.id
                }`
            )
        );
        this.page = page ?? 1;
        if (Array.isArray(sort)) {
            this.columnsSortOrder = sort;
            this.setColumnSortOptions();
        } else {
            this.setColumnSortDefaults();
        }
    }

    trackByUuid(index: number, item: DeliveryListItem): string {
        return item.uuid;
    }

    setListCrumbs() {
        DeliveriesListComponent.crumbsSubject.next({
            level: 0,
            crumbs: null,
        });

        DeliveriesListComponent.crumbsSubject.next({
            level: 1,
            crumbs: [{
                label: this.translateService.instant('SALES_DOCUMENT|Lista dostaw'),
                url: '/transport/deliveries',
            }],
        });
    }

    filterUndelivered() {
        this.filtered = !this.filtered;
        this.updateList();
    }
}
